/**
 *
 * slider-utils.js
 * Optimized implementation of the horizontalLoop helper function
 *
 * Creates slider controls for navigation
 * @param {string} sliderId - The ID selector for the slider
 * @param {Object} sliderInstance - The slider instance object
 */
export function createSliderControls(sliderId, sliderInstance) {
    const controls = document.querySelectorAll(`[data-controls="${sliderId}"]`);
  
    if (!controls.length) {
      console.warn(`No controls found for slider: ${sliderId}`);
      return;
    }
  
    if (!sliderInstance) {
      console.warn(`Slider instance not found for slider: ${sliderId}`);
      return;
    }
  
    const { goToSlide, goToNext, goToPrevious, restartAutoplay } = sliderInstance;
  
    controls.forEach((control) => {
      const direction = control.getAttribute("data-control") === "next" ? 1 : -1;
  
      // Remove existing event listeners to prevent duplicates
      const newControl = control.cloneNode(true);
      control.parentNode.replaceChild(newControl, control);
  
      newControl.addEventListener("click", () => {
        if (direction === 1) goToNext();
        else goToPrevious();
  
        // Reset autoplay cycle immediately after advancing
        if (restartAutoplay) restartAutoplay();
      });
    });
  }
  
  /**
   * Register a slider instance in the global registry
   * @param {Object} sliderInstance - The slider instance to register
   */
  export function registerSlider(sliderInstance) {
    if (!window.sliderInstances) {
      window.sliderInstances = [];
    }
    
    window.sliderInstances.push(sliderInstance);
  }
  
  /**
   * Refresh all registered sliders
   * @param {Object} newSettings - Optional new settings to apply
   */
  export function refreshAllSliders(newSettings = {}) {
    if (window.sliderInstances && window.sliderInstances.length) {
      window.sliderInstances.forEach((instance) => {
        if (typeof instance.refresh === "function") {
          instance.refresh(newSettings);
        }
      });
    }
  }

  /**
 * Creates a progress bar for sliders
 * @param {string} sliderId - The ID selector for the slider
 * @param {Object} sliderInstance - The slider instance object
 */
export function createSliderProgressBar(sliderId, sliderInstance) {
  // Find progress bar container with data-progress attribute
  const progressBar = document.querySelector(`[data-progress="${sliderId}"]`);

  if (!progressBar) {
    console.warn(`No progress bar found for slider: ${sliderId}`);
    return;
  }

  if (!sliderInstance) {
    console.warn(`Slider instance not found for slider: ${sliderId}`);
    return;
  }

  // Get the number of slides in the slider
  const totalSlides = sliderInstance.totalSlides || 
                      (sliderInstance.getLoop && sliderInstance.getLoop().times ? 
                      sliderInstance.getLoop().times.length : 0);
  
  if (!totalSlides) {
    console.warn(`Could not determine number of slides for slider: ${sliderId}`);
    return;
  }

  // Clear any existing progress indicators
  progressBar.innerHTML = '';

  // Create individual progress indicators for each slide
  for (let i = 0; i < totalSlides; i++) {
    const indicator = document.createElement('div');
    indicator.className = 'progress-indicator';
    indicator.dataset.index = i;
    
    // Set first indicator as active
    if (i === 0) {
      indicator.classList.add('active');
    }
    
    // Add click event to navigate to that slide when clicked
    indicator.addEventListener('click', () => {
      sliderInstance.goToSlide(i);
      updateActiveIndicator(i);
    });
    
    progressBar.appendChild(indicator);
  }

  // Function to update the active indicator
  function updateActiveIndicator(index) {
    // Remove active class from all indicators
    const indicators = progressBar.querySelectorAll('.progress-indicator');
    indicators.forEach(ind => ind.classList.remove('active'));
    
    // Add active class to current indicator
    const currentIndex = index % totalSlides; // Ensure index wraps around
    indicators[currentIndex].classList.add('active');
  }

  // Add an event listener to the sliderInstance to update the progress bar
  const onSlideChangeHandler = (event) => {
    if (event && typeof event.detail !== 'undefined') {
      updateActiveIndicator(event.detail.index);
    }
  };

  // Add event listener to slider container
  const container = document.querySelector(sliderId);
  if (container) {
    container.addEventListener('slideChange', onSlideChangeHandler);
  }

  // Store the updateActiveIndicator function on the slider instance
  // so it can be called directly from the slider code
  sliderInstance.updateProgressBar = updateActiveIndicator;
  
  // Initial update of the progress bar
  const currentIndex = sliderInstance.currentIndex ? sliderInstance.currentIndex() : 0;
  updateActiveIndicator(currentIndex);
}

/**
 * Creates a unified visibility detection system for sliders
 * Uses IntersectionObserver for reliable detection with absolutely-positioned
 * containers and ScrollSmoother environments.
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.container - The container element to observe
 * @param {Function} options.onVisibilityChange - Callback when visibility changes
 * @param {boolean} options.debug - Enable debug logging
 * @returns {Object} Visibility controller with cleanup method
 */
export function createVisibilityDetection({ container, onVisibilityChange, debug = false }) {
  if (!container || typeof onVisibilityChange !== 'function') {
    console.warn('createVisibilityDetection: container and onVisibilityChange are required');
    return null;
  }

  // Helper function for debug logging
  const debugLog = (...args) => {
    if (debug) {
      console.log('[VisibilityDetection]', ...args);
    }
  };

  let observer = null;
  let isVisible = true; // Default to visible

  // Use IntersectionObserver — works reliably with position:absolute containers
  // and ScrollSmoother environments where ScrollTrigger can lose track.
  if (typeof IntersectionObserver !== 'undefined') {
    debugLog("Using IntersectionObserver for visibility detection");

    observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const wasVisible = isVisible;
      isVisible = entry.isIntersecting;

      if (wasVisible !== isVisible) {
        debugLog("IntersectionObserver visibility changed:", {
          wasVisible,
          isVisible,
          intersectionRatio: entry.intersectionRatio
        });

        onVisibilityChange(isVisible, {
          wasVisible,
          intersectionRatio: entry.intersectionRatio,
          method: 'IntersectionObserver'
        });
      }
    }, {
      // Fire as soon as even 1px enters/leaves the viewport
      threshold: 0
    });

    observer.observe(container);
    debugLog("IntersectionObserver visibility detection activated");
  } else {
    // Fallback for very old browsers without IntersectionObserver
    debugLog("IntersectionObserver not available, defaulting to always-visible");
  }

  // Return controller object with cleanup method
  return {
    isVisible: () => isVisible,
    destroy: () => {
      debugLog("Destroying visibility detection");

      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }
  };
}
